import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCServicesApi, arrayMove } from "react-common-components";
import { useForm } from "react-hook-form";
import SortableItem from "src/components/Sortable/Item";
import Title from "src/components/Title";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import SortableContainer from "src/components/Sortable/Container";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { IMatba001Form, IMatba004 } from "../interface";
import { Ulcontainer } from "../styles";
import Level3Change from "./level3Change";
import Level3List from "./level3List";
import { schemaNivel3 } from "../schemas";

export interface IRefLevel3{
    addItem: (maa003Id: number) => void;
  }

export interface IListOfLevel3{
    analysisData: IMatba001Form;
    data: IMatba004[];
	onCallbackEvaluation: () => void;
}

const ListOfLevel3: React.ForwardRefRenderFunction<IRefLevel3, IListOfLevel3> = ({
	analysisData,
	data,
	onCallbackEvaluation
}, ref) => {
	const { translate } = useTranslation();
	const {confirm} = useConfirmModal();
	const { addToast} = RCCHookToast();
	
	const [waitingForResponse, setWaitingForResponse] = useState(false);
	const [ tempData , setTempData ] = useState<IMatba004[] | null>(null);
	const [level3, setLevel3] = useState(data.map((item) => ({
		...item,
		id: String(item.maa004Id),
		onChange: false
	})));

	const {
		register,
		unregister,
		getValues,
		control,
		formState:{errors},
		setError,
		clearErrors,
		watch
	}= useForm();

	const addItem = (maa003Id: number) => {
		unregister();
		clearErrors();
		const newItem = {
			id: v4(),
			onChange: true,
			maa004Id: 0,
			maa004Maa003Id: maa003Id,
			maa004Descricao: "",
			maa004Ameaca: "N",
			maa004Oportunidade: "N",
			maa004Forca: "N",
			maa004Fraqueza: "N",
			maa004Importancia: 1,
			maa004Probabilidade: 1,
			maa004Tendencia: 1,
			maa004Pontuacao: 1,
			maa004Comentario: "",
			maa004Ordem: 1
		};
		setLevel3((prevState) => [newItem,...prevState]);
	};

	const copyItem = (matba004: IMatba004) => {
		unregister();
		const newItem = {
			...matba004,
			id: v4(),
			onChange: true,
			maa004Descricao: `${translate("CÓPIA")} ${matba004.maa004Descricao}`,
			maa004Id: 0
		};
		setLevel3((prevState) => [newItem,...prevState]);
	};

	const saveItem = async(index:number) => {
		const row = getValues(`[${index}]`);
		try {
			await schemaNivel3(translate).validate({...row},{
				abortEarly: false
			});

			if(analysisData.hasSwot){
				row.maa004Oportunidade = row.maa004Oportunidade ? "S" : "N";
				row.maa004Ameaca = row.maa004Ameaca ? "S" : "N";
				row.maa004Forca = row.maa004Forca ? "S" : "N";
				row.maa004Fraqueza = row.maa004Fraqueza ? "S" : "N";
			
				const quadrantSwot = [row.maa004Oportunidade, row.maa004Ameaca, row.maa004Forca, row.maa004Fraqueza];
				if(quadrantSwot.every((x) => x === "N")){
					addToast({
						color: "yellow",
						description: translate("Selecione ao menos um quadrante da SWOT"),
						title: translate("Atenção")
					});
					return;
				}
			}else{
				row.maa004Oportunidade = "N";
				row.maa004Ameaca = "N";
				row.maa004Forca = "N";
				row.maa004Fraqueza = "N";
			}

			row.maa004Pontuacao = analysisData.maa001Gut === "S" ? row.maa004Importancia * row.maa004Probabilidade * row.maa004Tendencia : 0;

			let response;
			if(row.maa004Id === "0"){

				response = await RCCServicesApi.postRequest({
					method: "Analysis/InsertLevel3",
					body: {
						...row	
					}
				});

			}else{
				response = await RCCServicesApi.putRequest({
					method: "Analysis/UpdateLevel3",
					body: {
						...row
					}
				});
			}

			const level3Data = response.data as IMatba004;
			
			addToast({
				color: "green",
				description: row.maa004Id === "0" ? translate("Item adicionado com sucesso") : translate("Item alterado com sucesso"),
				title: translate("Sucesso")
			});
			
			if(row.maa004Id === "0")
				row.maa004Id = response.data;
		
			setLevel3((prevState) => 
				prevState.map((x) => x.id === row.id ? 
					{
						...level3Data,
						id: row.id,
						onChange: false
					}
					: 
					x
				)
			);
			
			clearErrors();

			if(analysisData.maa001Graficoradar)
				onCallbackEvaluation();

		} catch (error) {
			if(getValidationErrors(error, setError))
				return;

			standardErrorMessages(error, row.maa004Id===0 ? translate("Erro ao adicionar item") : translate("Erro ao alterar item"), addToast);
		}
	};

	const changeItem = (row:IMatba004, change: boolean) => {
		unregister();
		setLevel3((prevState) => prevState.map((x) => x.id === row.id ? {
			...row,
			onChange:change
		} : x));
	};

	const deleteItem = async(row:IMatba004, useRequest:boolean=false) => {
		if(useRequest){
			confirm(
				translate("Exclusão de {{label}}", {label: analysisData.maa001Label3}),
				`${translate("Deseja realmente excluir {{label}}", {label:analysisData.maa001Label3})}: ${row.maa004Descricao} ?`,
				async () => {
					await RCCServicesApi.deleteRequest({
						method: "Analysis/DeleteLevel3",
						urlParameters: {
							id: row.maa004Id
						}
					});

					addToast({
						color: "green",
						description: translate("{{label}} excluído(a) com sucesso", {label: analysisData.maa001Label3}),
						title: translate("Sucesso")
					});

					unregister();
					setLevel3((prevState) => 
						prevState.filter((x) => x.id !== row.id)
					);

					if(analysisData.maa001Graficoradar)
						onCallbackEvaluation();
				},
				(err) => {
					standardErrorMessages(err, translate("Erro ao excluir {{label}}", {label: analysisData.maa001Label3}), addToast);
				},
				true
			);
		}else{
			unregister();
			setLevel3((prevState) => 
				prevState.filter((x) => x.id !== row.id)
			);
		}
	};

	const queryReorderLevel3 = async (levels3: any[]) => {
		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "Analysis/UpdateOrderLevel3",
				body: {
					rows: levels3
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: translate("{{label}} movido(a) com sucesso", {label: analysisData.maa001Label3}),
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setLevel3(tempData || []);
			standardErrorMessages(err, translate("Erro ao mover {{label}}", {label: analysisData.maa001Label3}), addToast);
			setWaitingForResponse(false);
			return false;
		}
	};

	useImperativeHandle(ref, () => ({
		addItem
	}));

	const ListLevel3Memo = useMemo(() => (
		<>
			{level3 && level3.map((item,index) => 
				(
					<SortableItem 
						id={item.maa004Id.toString()} 
						key={v4()} 
						disabled={waitingForResponse}
					>
						<li className="list-component-child" key={v4()}>
							{item.onChange ? 
								<>
									<Level3Change 
										analysisData={analysisData}
										index={index}
										item={item}
										register={register}
										control={control}
										errors={errors}
										watch={watch}
										saveItem={saveItem}
										changeItem={changeItem}
										deleteItem={deleteItem}
									/>
								</>
								:
								<>
									<Level3List
										analysisData={analysisData}
										item={item}
										changeItem={changeItem}
										copyItem={copyItem}
										deleteItem={deleteItem}
									/>
								</>									
							}
						</li>
					</SortableItem>
				)
			)}
		</>
		
	), [level3, errors]);

	return(
		<>
			{!level3 || level3.length === 0 &&
				<RCCGrid 
					container 
					item
					xs={12}
					styleInline="margin: 10px"
				>
					<RCCAlert 
						styleInline="width: 100%"
						title={translate("Atenção")}
						description={translate("Nenhum(a) {{label}} encontrado(a)", {label: analysisData.maa001Label3})}
					/>
				</RCCGrid>
			}

			<div className="container-actions">
				{
					level3 && level3.length > 0 &&
					<RCCGrid 
						container
						item
						xs={12}
					>
						<ul className="ul-businessModel-head">
							<li className="businessModel-head">
								<RCCGrid 
									container
									item
									justifyContent="space-between"
									alignItems="center"
									xs={12}
								>
									<span className="head-level-1">{analysisData.maa001Label3}</span>
								</RCCGrid>
							</li>
						</ul>
					</RCCGrid>
				}
										

				<SortableContainer
					onDragStart={() => {
						setTempData(data);
					}}
					onDragEnd={(activeIdX, overId) => {
						if(activeIdX === overId)
							return; 	

						const activeIndex = level3.findIndex((checks) => checks.id === activeIdX);
						const overIndex = level3.findIndex((checks) => checks.id === overId);

						const orderedItems = arrayMove(level3, activeIndex, overIndex);

						setLevel3(orderedItems);

						const rowsOrdered: any[] = [];

						orderedItems.forEach((item: any, index) => {
							const row = {
								maa004Id: item.maa004Id,
								maa004Descricao: item.maa004Descricao,
								maa004Ordem: index
							};
							rowsOrdered.push(row);
						});

						queryReorderLevel3(rowsOrdered);
					}}
					items={level3?.map((line) => ({
						...line,
						id: `${line.maa004Id.toString()}`
					})) || []}
					modifiers={[restrictToVerticalAxis]}
  			>
				
					<RCCGrid container item>
						<Ulcontainer>
							{
								level3 && level3.length > 0 &&
								<li className="list-component-header" key={v4()}>
									{
										analysisData.hasSwot ?
											<RCCGrid item container
												xs={8}
												sm={analysisData.maa001Gut === "S" ? 2 : 5}
												alignItems="baseline"
											>
												<Title color="defaultColor" size="sm">
													{translate("Descrição")}
												</Title>
											</RCCGrid>
											:
											<RCCGrid item container
												xs={8}
												sm={analysisData.maa001Gut === "S" ? 4 : 5}
												alignItems="baseline"
											>
												<Title color="defaultColor" size="sm">
													{translate("Descrição")}
												</Title>
											</RCCGrid>
									}
									{
										analysisData.hasSwot &&
										<RCCGrid item container
											xs={8}
											sm={analysisData.maa001Gut === "S" ? 2 : 6}
											alignItems="baseline"
										>
											<Title color="defaultColor" size="sm">
												{translate("SWOT")}
											</Title>
										</RCCGrid>
									}
									{
										analysisData.maa001Gut === "S" &&
										<>
											<RCCGrid item container
												xs={8}
												sm={2}
												alignItems="baseline"
											>
												<Title color="defaultColor" size="sm">
													{translate("Importância")}
												</Title>
												<RCCPopover
													header={{title:translate("Importância")}}
													body={{custom:
													<span className="popover-body">
														{translate("Aqui é preciso entender o quanto o item tem de impacto na estratégia da empresa")}, {translate("apartir de")} <b>{translate("Sem importância")}</b>, {translate("o valor mais baixo, até")} <b>{translate("Totalmente importante")}</b>, {translate("o valor mais alto")}.
													</span>
													}}
												>
													<RCCIcon
														name="infoCircle"
													/>
												</RCCPopover>
											</RCCGrid>
											<RCCGrid item container
												xs={8}
												sm={2}
												alignItems="baseline"
											>
												<Title color="defaultColor" size="sm">
													{translate("Intensidade")}
												</Title>
												<RCCPopover
													header={{title: translate("Intensidade")}}
													body={{text:`${translate("O quanto o item pode escalar ou diminuir de impacto na sua estratégia")}, ${translate("com o valor mais baixo representando que ele não tem como impactar mais e o valor mais alto representando que ele ainda pode impactar muito")}.`}}
												>
													<RCCIcon
														name="infoCircle"
													/>
												</RCCPopover>
											</RCCGrid>
											<RCCGrid item container
												xs={8}
												sm={2}
												alignItems="baseline"
											>
												<Title color="defaultColor" size="sm">
													{translate("Tendência")}
												</Title>
												<RCCPopover
													header={{title:translate("Tendência")}}
													body={{text:`${translate("Aqui será avaliado o quanto o item tem a tendência de se manter ou deixar de existir")}. ${translate("A tendência de se manter é o valor mais alto e a tendência de deixar de existir é o valor mais baixo")}.`}}
												>
													<RCCIcon
														name="infoCircle"
													/>
												</RCCPopover>
											</RCCGrid>
										</>
									}
									
									<RCCGrid item container
										xs={8}
										sm={1}
										alignItems="baseline"
									>
										<Title color="defaultColor" size="sm">
											{translate("Pontos")}
										</Title>
									</RCCGrid>
								</li>
							}

							{ListLevel3Memo}

						</Ulcontainer>
					</RCCGrid>
				</SortableContainer>										
			</div>
		</>
	);
};

export default forwardRef(ListOfLevel3);

